import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Stack, Typography, TextField, InputAdornment,
  Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
  IconButton, TablePagination, LinearProgress,
  Button,
} from '@mui/material';
import {
  Search, Clear, FilterAltOff, Groups as GroupsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';

import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import {
  AdmissionCycle, AdmissionApplication, ApplicationStatus, Pagination,
} from '../../types/admissions';
import StatusChip, { statusLabel } from '../common/StatusChip';
import SearchableSelect, { SelectOption } from '../common/SearchableSelect';
import SearchableMultiSelect from '../common/SearchableMultiSelect';
import ApplicationActionsMenu from './ApplicationActionsMenu';
import { TabToolbar, tableHeadCellSx, RowActionsCell, useCycleClassOptions } from './tabLayout';
import PersonAvatar from '../common/PersonAvatar';
import { photoFilenameOf } from '../../utils/personPhoto';
import EmptyState from '../common/EmptyState';
import TableRowSkeletons from '../common/TableRowSkeletons';
import { useDebounce } from '../../hooks/useDebounce';
import dateTimeUtils from '../../utils/dateTimeUtils';
import CandidateDrawer from './CandidateDrawer';
import { ApplicationsListDocument } from './pdf';
import { usePdfExport, useSchoolBranding, fmtClassName } from '../../utils/pdf';
import { writeSheet, downloadXlsxBlob } from '../../utils/xlsx';
import ExportMenuButton, { type ExportKind } from '../common/ExportMenuButton';
import FormLinksStrip from './formLinks/FormLinksStrip';
import { applicationExportColumns, classNameOf } from '../../utils/admissionRecords';

/**
 * ApplicationsTab — the daily operator surface. Lives inside
 * AdmissionCycleDetailPage. Filters are URL-scoped under this tab so a
 * specific view (e.g. "DocsRequired in Grade 5") is linkable.
 *
 * Row click opens CandidateDrawer which handles all per-application
 * actions (document verify, score entry, concession approval, transition,
 * withdraw). This tab stays focused on list + filter + navigation.
 */

interface Props {
  cycle: AdmissionCycle;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// Status groupings for the multi-select. Keeps the dropdown scannable —
// 21 states would be a wall of text otherwise.
const STATUS_GROUPS: Array<{ label: string; values: ApplicationStatus[] }> = [
  { label: 'In progress', values: ['Draft', 'Submitted', 'AppFeePending', 'UnderReview', 'DocsRequired', 'AssessmentScheduled', 'Absent', 'Assessed'] },
  { label: 'Decision',    values: ['Offered', 'Waitlisted', 'Rejected', 'Ineligible'] },
  { label: 'Confirming',  values: ['OfferAccepted', 'ConfirmedConditional', 'AdmFeePending', 'Confirmed'] },
  { label: 'Final',       values: ['Enrolled', 'OfferDeclined', 'OfferLapsed', 'NotAdmitted', 'Withdrawn'] },
];
const ALL_STATUSES: ApplicationStatus[] = STATUS_GROUPS.flatMap(g => g.values);
const STATUS_GROUP_OF = Object.fromEntries(
  STATUS_GROUPS.flatMap(g => g.values.map(v => [v, g.label])),
) as Record<ApplicationStatus, string>;
const statusOptions: SelectOption[] = ALL_STATUSES.map(v => ({
  value: v,
  label: statusLabel({ kind: 'application', value: v }),
}));

const ApplicationsTab: React.FC<Props> = ({ cycle }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters mirrored under this tab's URL namespace.
  const [statuses, setStatuses] = useState<ApplicationStatus[]>(
    () => (searchParams.get('status')?.split(',').filter(Boolean) as ApplicationStatus[]) ?? [],
  );
  const [classId, setClassId] = useState<string>(searchParams.get('class') ?? '');
  const [searchInput, setSearchInput] = useState<string>(searchParams.get('q') ?? '');
  const search = useDebounce(searchInput, 300);
  // Ticking several statuses in a row fires one request, not one per tick.
  const statusFilter = useDebounce(statuses, 350);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const parsed = parseInt(searchParams.get('limit') ?? '25', 10);
    return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 25;
  });

  const [rows, setRows] = useState<AdmissionApplication[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);

  // Drawer state.
  const [openId, setOpenId] = useState<string | null>(null);
  // Row ⋮ menu — one shared menu, anchored to whichever row opened it.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; app: AdmissionApplication } | null>(null);

  // Sync URL on filter change. Functional update so keys owned by the page
  // (?tab=) survive — a stale copy of the params would drop them.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (statusFilter.length) next.set('status', statusFilter.join(',')); else next.delete('status');
      if (classId) next.set('class', classId); else next.delete('class');
      if (search.trim()) next.set('q', search.trim()); else next.delete('q');
      if (rowsPerPage !== 25) next.set('limit', String(rowsPerPage)); else next.delete('limit');
      return next;
    }, { replace: true });
  }, [statusFilter, classId, search, rowsPerPage, setSearchParams]);

  // Reset page when filters change.
  useEffect(() => { setPage(0); }, [statusFilter, classId, search]);

  // Only the newest request may update the table — an older, slower
  // response must never overwrite fresher results.
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await admissionApplicationService.list({
        cycleId: cycle._id,
        classId: classId || undefined,
        status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
        search: search.trim() || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (seq !== requestSeq.current) return;
      setRows(res.items);
      setPagination(res.pagination);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      enqueueSnackbar(err?.message || 'Failed to load applications', { variant: 'error' });
      setRows([]);
      setPagination(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [cycle._id, classId, statusFilter, search, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const filtersActive = useMemo(
    () => statuses.length > 0 || !!classId || !!search.trim(),
    [statuses, classId, search],
  );

  // ── Export — every application matching the current filters ──────────
  const { branding } = useSchoolBranding();
  const { generate: generatePdf } = usePdfExport();

  const fetchForExport = () => admissionApplicationService.listAllWithDetails({
    cycleId: cycle._id,
    classId: classId || undefined,
    status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
    search: search.trim() || undefined,
  });
  /** "Class 9 · Under review, Offered · “ali”" — what the file contains. */
  const filterSummary = () => [
    classId ? fmtClassName(classOptions.find(c => c._id === classId)?.name) : '',
    statusFilter.map(s => statusLabel({ kind: 'application', value: s })).join(', '),
    search.trim() ? `“${search.trim()}”` : '',
  ].filter(Boolean).join('  ·  ');
  const fileBase = `applications-${cycle.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()}`;

  const exportAs = async (kind: ExportKind) => {
    try {
      if (kind === 'xlsx') {
        const { items: apps, users } = await fetchForExport();
        const columns = applicationExportColumns(cycle, users);
        const blob = await writeSheet({
          sheetName: 'Applications',
          columns: columns.map(c => ({ header: c.header, width: c.width })),
          rows: apps.map(app => columns.map(c => c.value(app))),
          emptyRowsForValidation: 0,
        });
        downloadXlsxBlob(`${fileBase}.xlsx`, blob);
      } else {
        await generatePdf(async () => (
          <ApplicationsListDocument
            apps={(await fetchForExport()).items}
            cycle={cycle}
            branding={branding}
            meta={{
              title: 'Applications',
              subtitle: [cycle.name, filterSummary()].filter(Boolean).join('  ·  '),
              generatedAt: new Date().toISOString(),
            }}
          />
        ), `${fileBase}.pdf`);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not export the applications', { variant: 'error' });
    }
  };

  const resetFilters = () => {
    setStatuses([]);
    setClassId('');
    setSearchInput('');
  };

  const classOptions = useCycleClassOptions(cycle);

  const closeDrawer = () => setOpenId(null);
  const refreshAfterAction = () => load();

  return (
    <Box>
      {/* ── Filter row ──────────────────────────────────────────────── */}
      <TabToolbar
        onRefresh={load}
        refreshing={loading}
        actions={(
          <ExportMenuButton count={pagination?.totalItems ?? 0} noun="applications" filtered={filtersActive} onExport={exportAs} />
        )}
      >
          <TextField
            size="small"
            placeholder="Search by name, application no. or father's name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <SearchableMultiSelect
            label="Status"
            values={statuses}
            onChange={(v) => setStatuses(v as ApplicationStatus[])}
            options={statusOptions}
            groupBy={(o) => STATUS_GROUP_OF[o.value as ApplicationStatus]}
            placeholder="All"
            minWidth={260}
            width={260}
            maxChipsShown={1}
          />
          {classOptions.length > 0 && (
            <SearchableSelect
              label="Class"
              value={classId}
              onChange={setClassId}
              options={[
                { value: '', label: 'All' },
                ...classOptions.map(c => ({ value: c._id, label: c.name })),
              ]}
              placeholder="All classes"
              minWidth={180}
            />
          )}
          {/* Space is always reserved so the fields don't shift when it appears. */}
          <Button
            size="small"
            variant="text"
            onClick={resetFilters}
            startIcon={<FilterAltOff fontSize="small" />}
            sx={{ visibility: filtersActive ? 'visible' : 'hidden', flexShrink: 0 }}
          >
            Reset
          </Button>
      </TabToolbar>

      {/* Form links the desk sent, still with the families. */}
      <FormLinksStrip cycleId={cycle._id} />

      {/* ── Table ──────────────────────────────────────────────────── */}
      {/* Thin progress bar during refetch so the filter change feels alive
          while the existing rows stay in place. */}
      <Box sx={{ position: 'relative' }}>
      {loading && rows.length > 0 && (
        <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }} />
      )}
      <TableContainer sx={{ opacity: loading && rows.length > 0 ? 0.6 : 1, transition: 'opacity 150ms ease' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': tableHeadCellSx }}>
              <TableCell>Application no.</TableCell>
              <TableCell>Applicant</TableCell>
              <TableCell>Father</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Merit</TableCell>
              <TableCell align="right">&nbsp;</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRowSkeletons columns={8} rows={6} />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 6 }}>
                  <EmptyState
                    icon={<GroupsIcon />}
                    title={filtersActive ? 'No applications match these filters' : 'No applications yet'}
                    description={filtersActive
                      ? 'Clear the filters to see all applications.'
                      : cycle.status === 'Open'
                        ? 'Share the public apply link with parents, or create a walk-in application.'
                        : 'Open the cycle to start receiving applications.'}
                    action={filtersActive
                      ? { label: 'Reset filters', onClick: resetFilters }
                      : undefined}
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((app) => (
                <TableRow
                  key={app._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setOpenId(app._id)}
                >
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600} noWrap>
                      {app.applicationNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <PersonAvatar name={app.name} photoFilename={photoFilenameOf(app)} size={30} />
                      <Typography variant="body2" fontWeight={600}>{app.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{app.fatherName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{classNameOf(app)}</Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={{ kind: 'application', value: app.status }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {dateTimeUtils.formatDateForDisplay(app.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontFamily="monospace">
                      {app.computedMeritScore != null ? app.computedMeritScore.toFixed(2) : '—'}
                    </Typography>
                  </TableCell>
                  <RowActionsCell app={app} onMenu={(anchor) => setRowMenu({ anchor, app })} />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
      {pagination && pagination.totalItems > 0 && (
        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
      )}

      <ApplicationActionsMenu
        app={rowMenu?.app ?? null}
        cycle={cycle}
        anchorEl={rowMenu?.anchor ?? null}
        onClose={() => setRowMenu(null)}
        onMutated={refreshAfterAction}
      />

      {/* ── Candidate drawer ─────────────────────────────────────── */}
      {openId && (
        <CandidateDrawer
          applicationId={openId}
          cycle={cycle}
          onClose={closeDrawer}
          onMutated={refreshAfterAction}
        />
      )}
    </Box>
  );
};


export default ApplicationsTab;
